import { mapState, mapGetters, mapActions } from 'vuex';
import { isMobile } from 'mobile-device-detect';
import sleep from 'sleep-promise';
import appConfig from '../config/app';
import { useUsernameStorage, usePaymentStorage } from '../utils/useLocalStorage';

const TRIGGER_USERNAME_TIMEOUT = 2000;

export default {
  data() {
    const usernameStorage = useUsernameStorage();
    const paymentStorage = usePaymentStorage();

    return {
      isMobile,
      currentPayment: 0,
      sleepLoading: 500,
      other: {
        contact: appConfig.contact,
        sound: appConfig.sound.paymentSuccess,
      },
      usernameStorage,
      paymentStorage,
    };
  },

  computed: {
    ...mapState('payment', [
      'loading',
      'current',
      'triggerUsername',
      'currency',
      'payment',
      'option',
      'steps',
      'confirmBox',
      'paymentProcess',
      'paymentSuccess',
      'pusher',
    ]),

    ...mapState('notification', ['headerNotify']),

    activeService: {
      get() {
        return this.$store.state.payment.activeService;
      },
      set(value) {
        this.$store.commit('payment/SET_ACTIVE_SERVICE', value);
      },
    },

    ...mapGetters('payment', [
      'paymentMethodName',
      'paymentMethodText',
      'paymentMethodId',
      'paymentTotal',
      'paymentNote',
      'paymentQrCode',
      'findServiceData',
    ]),

    paymentStepHeader() {
      const { username } = this.payment || {};
      const serviceId = this.payment?.service?.id;

      if (!username) {
        return this.$t('PLEASE_ENTER_USERNAME');
      }

      if (!serviceId) {
        return this.$t('PLEASE_SELECT_SERVICE');
      }

      const serviceData = this.findServiceData;

      return this.$t('PAYMENT_STEP_TEXT', {
        username,
        service: serviceData.title,
        total: this.$options.filters.addCommas(serviceData.price),
      });
    },

    paymentPreNote() {
      return this.$t('PAYMENT_PROCCESS_NOTE', {
        note: this.$t('PAYMENT_PROCCESS_VALUE_BY_NOTE'),
        money: this.$t('PAYMENT_PROCCESS_VALUE_BY_MONEY'),
      });
    },

    paymentFinish() {
      return this.$t('PAYMENT_PROCCESS_AFTER_TRANSFER_NOTE');
    },

    paymentContact() {
      return this.$t('PAYMENT_CONTACT', {
        contact: this.other.contact.phone,
      });
    },

    paymentUsername: {
      get() {
        return this.payment?.username || '';
      },
      set(value) {
        this.$store.commit('payment/UPDATE_PAYMENT_FIELD', {
          field: 'username',
          value,
        });
      },
    },

    paymentServiceId: {
      get() {
        return this.payment?.service?.id || '';
      },
      set(value) {
        this.$store.commit('payment/UPDATE_PAYMENT_FIELD', {
          field: 'service.id',
          value,
        });
      },
    },

    paymentMethodId: {
      get() {
        return this.payment?.method?.id || '';
      },
      set(value) {
        this.$store.commit('payment/UPDATE_PAYMENT_FIELD', {
          field: 'method.id',
          value,
        });
      },
    },
  },

  methods: {
    ...mapActions('payment', [
      'initializePayment',
      'initializeSteps',
      'fetchMethodTransfer',
      'fetchListService',
      'onChangeService',
      'onChangeMethod',
      'runStepPayment',
      'saveUsernameToStorage',
      'savePaymentDataToStorage',
      'updateFirstStep',
      'findServiceAndActiveKey',
    ]),

    ...mapActions('notification', [
      'fetchNotify',
      'openNotify',
      'closeNotify',
    ]),

    openUrl(url) {
      window.open(url, '_blank');
    },

    async getParam() {
      this.$store.commit('payment/SET_LOADING', true);
      this.$store.commit('payment/SET_CURRENT', Number(this.$route.query.step) || 0);

      await this.fetchInitialData();
      await this.initializeSteps(this.$i18n);
      await this.initializeActiveService();
      await this.handleMethodInitialization();
      await this.handleServiceInitialization();
      await this.handleUsernameValidation();

      await sleep(this.sleepLoading);
      this.$store.commit('payment/SET_LOADING', false);
    },

    async fetchInitialData() {
      await this.fetchNotify();
      await this.fetchListService();
      await this.fetchMethodTransfer();
    },

    async initializeActiveService() {
      const { activeService, option } = this.$store.state.payment;

      if (!activeService && option.service?.length) {
        const firstName = option.service[0]?.name || '';
        this.$store.commit('payment/SET_ACTIVE_SERVICE', firstName);
      }
    },

    async handleMethodInitialization() {
      const { payment, option } = this.$store.state.payment;
      const methods = option.method;

      if (payment?.method?.id) {
        try {
          const findMethod = methods.find(
            (method) => method.id === payment.method.id,
          );

          if (findMethod) {
            await this.onChangeMethod(findMethod);
          } else if (methods.length === 1) {
            // Only one method available (method step is skipped): always fall
            // back to it instead of leaving the payment without a method.
            await this.onChangeMethod(methods[0]);
          } else {
            this.resetMethodAndCurrent();
          }
        } catch (error) {
          console.error({
            msg: 'GET_METHOD',
            error,
          });
          this.resetMethodAndCurrent();
        }
      } else if (methods.length > 0) {
        await this.onChangeMethod(methods[0]);
      }
    },

    resetPaymentField(field) {
      this.$store.commit('payment/UPDATE_PAYMENT_FIELD', {
        field,
        value: '',
      });
      this.$store.commit('payment/SET_CURRENT', 0);
    },

    resetMethodAndCurrent() {
      this.resetPaymentField('method.id');
    },

    async handleServiceInitialization() {
      const { payment } = this.$store.state.payment;

      if (payment?.service?.id) {
        try {
          const { findService, activeService } = await this.findServiceAndActiveKey();

          if (findService && activeService) {
            await this.onChangeService(findService);
            this.$store.commit('payment/SET_ACTIVE_SERVICE', activeService);
          } else {
            this.$store.commit('payment/UPDATE_PAYMENT_FIELD', {
              field: 'service.id',
              value: '',
            });
          }
        } catch (error) {
          console.error({
            msg: 'GET_SERVICE',
            error,
          });
          this.resetServiceAndCurrent();
        }
      } else {
        this.resetServiceAndCurrent();
      }
    },

    resetServiceAndCurrent() {
      this.resetPaymentField('service.id');
    },

    async handleUsernameValidation() {
      const { payment } = this.$store.state.payment;

      if (!payment?.username) {
        this.$store.commit('payment/SET_CURRENT', 0);
      }
    },

    validStep() {
      const { current, steps } = this.$store.state.payment;
      const step = steps[current];

      if (!step) {
        return false;
      }

      if (step.id === 'service') {
        return this.validateServiceStep();
      }

      if (step.id === 'method') {
        return this.validateMethodStep();
      }

      return false;
    },

    validateServiceStep() {
      const { payment } = this.$store.state.payment;

      if (!payment?.username) {
        this.$store.commit('payment/SET_TRIGGER_USERNAME', true);

        setTimeout(() => {
          this.$store.commit('payment/SET_TRIGGER_USERNAME', false);
        }, TRIGGER_USERNAME_TIMEOUT);

        this.$message.error(this.$t('PLEASE_ENTER_USERNAME'));
        return false;
      }

      if (!payment?.service?.id) {
        this.$message.error(this.$t('PLEASE_SELECT_SERVICE'));
        return false;
      }

      return true;
    },

    validateMethodStep() {
      const { payment } = this.$store.state.payment;

      if (!payment?.method?.id) {
        this.$message.error(this.$t('PLEASE_SELECT_PAYMENT_METHOD'));
        return false;
      }

      return true;
    },

    async changeStep(direction) {
      this.$store.commit('payment/SET_LOADING', true);
      await sleep(this.sleepLoading);

      const { current } = this.$store.state.payment;
      const newCurrent = direction === 'next' ? current + 1 : current - 1;
      this.$store.commit('payment/SET_CURRENT', newCurrent);
      this.$store.commit('payment/SET_LOADING', false);
    },

    async next() {
      const isNext = this.validStep();

      if (!isNext) {
        return;
      }

      const { current, steps } = this.$store.state.payment;
      if (steps[current + 1]?.id === 'payment') {
        this.runStepPayment();
      }

      await this.changeStep('next');
    },

    async prev() {
      await this.changeStep('prev');
    },

    start() {
      this.$store.commit('payment/SET_CURRENT', 0);
    },

    async finish() {
      this.$store.commit('payment/SET_PAYMENT_PROCESS', { show: false });
      this.$store.commit('payment/SET_LOADING', true);
      await sleep(this.sleepLoading);
      const stepsLength = this.$store.state.payment.steps.length;
      this.$store.commit('payment/SET_CURRENT', stepsLength - 1);
      this.$store.commit('payment/SET_LOADING', false);
    },

    processConfirmPayment() {
      const { timeWait } = this.$store.state.payment.paymentProcess;
      this.$store.commit('payment/SET_PAYMENT_PROCESS', {
        show: true,
        countdown: Date.now() + timeWait,
      });
    },

    confirmPayment() {
      this.$store.commit('payment/SET_CONFIRM_BOX', true);
    },

    copyToClipboard(content) {
      this.$clipboard(content);
      this.$message.success(content);
      this.$message.info(this.$t('CONTENT_WAS_COPY_TO_CLIPBOARD'));
    },

    coloumService(ref) {
      const colum = {
        autoshopee: 6,
        subcheo: 6,
      };

      return colum[ref] || 8;
    },

    onChangeUsername() {
      this.updateFirstStep();
      this.saveUsernameToStorage();
    },

    updateRouter() {
      const { payment, current } = this.$store.state.payment;

      this.saveUsernameToStorage();
      this.savePaymentDataToStorage();

      this.$router.replace({
        path: this.$route.path,
        query: {
          username: payment?.username || '',
          step: current || 0,
          service: payment?.service?.id || '',
          method: payment?.method?.id || '',
        },
      }).catch(() => {});
    },

    updatePusher() {
      const { pusher, payment, currency } = this.$store.state.payment;

      if (!pusher.app || !payment?.username) {
        return;
      }

      if (pusher.channel) {
        pusher.channel.unbind(appConfig.service.pusher.event);
      }

      pusher.channel = pusher.app.subscribe(payment.username);

      const handler = ({
        username,
        money,
        service,
      }) => {
        const moneyText = [
          this.$options.filters.addCommas(money),
          currency,
        ].join(' ');

        const paymentSuccessMsg = this.$t('PAYMENT_AFTER_SUB', {
          service,
          username,
          money: moneyText,
        });

        this.$store.commit('payment/SET_PAYMENT_SUCCESS', paymentSuccessMsg);

        this.finish();
        this.playSoundDone().catch(() => {});
      };

      pusher.channel.bind(appConfig.service.pusher.event, handler);
      this.$store.commit('payment/SET_PUSHER_CHANNEL', pusher.channel);
    },

    async playSoundDone() {
      const audio = new Audio(this.other.sound);
      return audio.play();
    },

    openPopupOk() {
      const { headerNotify } = this.$store.state.notification;
      this.openUrl(headerNotify.url);
      this.closeNotify();
    },

    openPopupCancel() {
      this.closeNotify();
    },

    initializePusher() {
      this.$store.commit('payment/SET_PUSHER_APP', new Pusher(appConfig.service.pusher.key, {
        cluster: appConfig.service.pusher.cluster,
      }));

      const { payment } = this.$store.state.payment;

      if (payment?.username) {
        this.updatePusher();
      }
    },

    handlePopupNotification() {
      const { headerNotify } = this.$store.state.notification;

      if (!headerNotify.popupShow) {
        return;
      }

      const keyStore = 'payment-last-popup-shown';
      const lastShown = this.$cookies.get(keyStore);
      const now = Date.now();
      const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

      if (!lastShown || now - lastShown > TWELVE_HOURS_MS) {
        this.$store.commit('notification/SET_POPUP_VISIBLE', true);
        this.$cookies.set(keyStore, now, { expires: '12h' });
      }
    },
  },

  async created() {
    const initialStep = Number(this.$route.query.step) || 0;
    this.$store.commit('payment/SET_CURRENT', initialStep);

    await this.initializePayment({
      route: this.$route,
      i18n: this.$i18n,
    });

    // Steps are built inside getParam (after methods are fetched) so the
    // method step can be skipped when there is only one bank/method.
    await this.getParam();

    this.onChangeUsername();

    const { current, steps } = this.$store.state.payment;
    const paymentIndex = steps.findIndex((step) => step.id === 'payment');

    if (current > paymentIndex) {
      this.$store.commit('payment/SET_CURRENT', 0);
    } else if (current === paymentIndex) {
      await this.runStepPayment();
    }

    this.initializePusher();
    this.handlePopupNotification();
  },

  watch: {
    'payment.username': {
      handler(newVal) {
        if (newVal) {
          this.updatePusher();
          this.saveUsernameToStorage();
        }
      },
    },

    'payment.service.id': {
      handler() {
        this.updateRouter();
      },
    },

    'payment.method.id': {
      handler() {
        this.updateRouter();
      },
    },

    current: {
      handler() {
        this.updateRouter();
      },
    },
  },
};
